/**
 * Compare surfaces — the Add-ons section (spec §4.2, §12.4), grouped under
 * "Cut & fill", "Deviation map" and "Report & export".
 *
 * An add-on that depends on a result that is not ticked stays visible,
 * disabled, with "Needs Deviation map — Add it": the link ticks that result
 * (or, when the result is locked, opens the upgrade dialog). Volumes by
 * chainage, Expected thickness and DXF belong to cut & fill only (spec goal
 * 3); without it their group collapses to one line saying so, with the same
 * "Add it" link (C1).
 *
 * After "Add it" ticks the result, focus moves to the add-on's own control,
 * now enabled, so a keyboard user lands where they were heading.
 *
 * The section only edits the form; what is sent is decided at submit, where
 * every gate is re-read and a cut & fill add-on without cut & fill is
 * stripped (ruling R20).
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useWatch, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { LayerData } from '@/atoms/layers.atom';
import { AddOnToggle, AnalysisSection, StationVolumesAddOn } from '@/components/analyses/sections';
import { ExpectationFields } from '@/components/ExpectationFields';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { Alignment } from '@/core/alignment';
import type { FeatureResult } from '@/hooks/useFeature';
import { isPointcloudLayer } from '@/lib/analyses/candidates';
import type { CompareResult } from '@/lib/analyses/compare/availability';
import type { CompareFormValues } from '@/lib/analyses/compare/formSchema';
import { REPORT_LOCALE_NAMES, SUPPORTED_REPORT_LOCALES } from '@/lib/reportLocales';
import type { StationVolumesDerived, StationVolumesFormValues } from '@/lib/stationVolumes/dialogModel';

type Gate = Pick<FeatureResult, 'allowed' | 'gate' | 'promptUpgrade'>;

export interface CompareAddOnsProps {
  form: UseFormReturn<CompareFormValues>;
  /** The results the run will actually make (ticked, offered, not locked). */
  cutFillOn: boolean;
  deviationOn: boolean;
  /** Whether each result is on offer at all (flag on, inputs allow it). A
   *  group whose result is not on offer has nothing to add to. */
  cutFillOffered: boolean;
  deviationOffered: boolean;
  /** Ticks a result from an add-on's "Add it" link. False when it could
   *  not (the result is locked and the upgrade dialog opened instead). */
  onAddResult: (result: CompareResult) => boolean;
  design: LayerData | undefined;
  layers: LayerData[];
  alignments: ReadonlyMap<string, Alignment>;
  stationValues: StationVolumesFormValues;
  stationDerived: StationVolumesDerived;
  onStationChange: (patch: Partial<StationVolumesFormValues>) => void;
  stationGate: Gate;
  expectationGate: Gate;
  /** Why the PDF cannot be made right now, or undefined. */
  pdfDisabledReason?: ReactNode;
  /** The run name: the report title's placeholder (a blank title prints it). */
  runName: string;
}

function AddOnGroup({ title, testId, children }: { title: ReactNode; testId: string; children: ReactNode }) {
  return (
    <div data-testid={testId} className='flex min-w-0 flex-col gap-3'>
      {/* A sub-header under the section's own uppercase heading. */}
      <h4 className='text-sm font-semibold'>{title}</h4>
      {children}
    </div>
  );
}

export function CompareAddOns({
  form,
  cutFillOn,
  deviationOn,
  cutFillOffered,
  deviationOffered,
  onAddResult,
  design,
  layers,
  alignments,
  stationValues,
  stationDerived,
  onStationChange,
  stationGate,
  expectationGate,
  pdfDisabledReason,
  runName,
}: CompareAddOnsProps) {
  const { t } = useTranslation('project');
  const dxfId = useId();
  const rendersId = useId();
  const pdfId = useId();
  const tifId = useId();
  const [addOns, renderPerSurface, grid] = useWatch({
    control: form.control,
    name: ['addOns', 'deviation.renderPerSurface', 'cutFill.resolution'],
  });

  // "Add it" → focus that add-on's control once it is enabled. A request
  // counter keyed effect: the control only becomes enabled on the render
  // after the result is ticked.
  const sectionRef = useRef<HTMLDivElement>(null);
  const [focusRequest, setFocusRequest] = useState<{ n: number; addOn: string }>({ n: 0, addOn: '' });
  useEffect(() => {
    if (focusRequest.n === 0) return;
    // `addOn` may list several ("stations expectation dxf"): the first one
    // whose control is enabled wins (no alignments → chainage stays off).
    for (const addOn of focusRequest.addOn.split(' ')) {
      const box = sectionRef.current?.querySelector(`[data-compare-addon="${addOn}"]`);
      // The add-on's switch, else (Expected thickness) its selected answer.
      const target =
        box?.querySelector<HTMLElement>('[role="checkbox"]:not(:disabled)') ??
        box?.querySelector<HTMLElement>('[role="radio"][data-state="checked"]:not(:disabled)');
      if (target) {
        target.focus();
        return;
      }
    }
  }, [focusRequest]);
  const set = (name: 'addOns.dxf' | 'addOns.pdf' | 'addOns.geotiff' | 'deviation.renderPerSurface', value: boolean) =>
    form.setValue(name, value, { shouldDirty: true });

  const resultTitle = (result: CompareResult) =>
    result === 'cutFill' ? t('compareSurfaces.results.cutFill.title') : t('compareSurfaces.results.deviation.title');

  /** "Add it": ticks `result`, then focuses the add-on `addOn`. */
  const addItButton = (result: CompareResult, addOn: string) => (
    <button
      type='button'
      data-testid={`compare-add-${result}`}
      // "Add it" alone does not say what it adds.
      aria-label={t('compareSurfaces.addOns.addResultLabel', { result: resultTitle(result) })}
      className='rounded-sm font-medium underline underline-offset-2 hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
      onClick={() => {
        if (onAddResult(result)) setFocusRequest((prev) => ({ n: prev.n + 1, addOn }));
      }}>
      {t('compareSurfaces.addOns.addIt')}
    </button>
  );

  /** "Needs Deviation map — Add it", for the add-on `addOn`. */
  const needs = (result: CompareResult, addOn: string) => (
    <>
      {result === 'cutFill' ? t('compareSurfaces.addOns.needsCutFill') : t('compareSurfaces.addOns.needsDeviation')}
      {' — '}
      {addItButton(result, addOn)}
    </>
  );

  const pcDesign = !!design && isPointcloudLayer(design);

  return (
    <AnalysisSection title={t('analysisConfig.sections.addOns')} testId='compare-section-addons'>
      <div ref={sectionRef} className='flex min-w-0 flex-col gap-5'>
        {cutFillOffered && !cutFillOn && (
          // A deviation-only run: the group collapses to one line instead of
          // three disabled add-ons each saying the same thing (C1).
          <AddOnGroup title={t('compareSurfaces.part.cutFill')} testId='compare-addons-group-cutFill'>
            <p data-testid='compare-addons-cutFill-needs' className='text-xs break-words text-muted-foreground'>
              {t('compareSurfaces.addOns.cutFillGroupNeeds')}
              {' — '}
              {addItButton('cutFill', 'stations expectation dxf')}
            </p>
          </AddOnGroup>
        )}
        {cutFillOffered && cutFillOn && (
          <AddOnGroup title={t('compareSurfaces.part.cutFill')} testId='compare-addons-group-cutFill'>
            <div data-compare-addon='stations' className='min-w-0'>
              <StationVolumesAddOn
                alignments={alignments}
                values={stationValues}
                onChange={onStationChange}
                resolution={grid}
                derived={stationDerived}
                gate={stationGate}
                referenceLayerId={design?.id}
              />
            </div>

            {/* Tolerance bands: no toggle of its own — "Nothing in particular"
              is the off state. */}
            <div
              data-testid='compare-addon-expectation'
              data-compare-addon='expectation'
              className='flex min-w-0 flex-col gap-1'>
              <div className='text-sm font-medium'>{t('compareSurfaces.addOns.expectedThickness')}</div>
              <ExpectationFields
                form={form}
                allowSurfaces
                layers={layers}
                gate={expectationGate}
                effectiveResolutionM={grid}
              />
            </div>

            <div data-compare-addon='dxf' className='min-w-0'>
              <AddOnToggle
                id={dxfId}
                testId='compare-addon-dxf'
                label={t('analysisConfig.addOns.exportDxf')}
                checked={addOns.dxf}
                onCheckedChange={(c) => set('addOns.dxf', c)}
              />
            </div>
          </AddOnGroup>
        )}

        {deviationOffered && (
          <AddOnGroup title={t('compareSurfaces.part.deviation')} testId='compare-addons-group-deviation'>
            {/* Image size and colour ramp live under Advanced. */}
            <div data-compare-addon='renders' className='min-w-0'>
              <AddOnToggle
                id={rendersId}
                testId='compare-addon-renders'
                label={pcDesign ? t('deviationAnalysis.renderOverviewPc') : t('deviationAnalysis.renderPerSurface')}
                help={
                  pcDesign ? t('deviationAnalysis.renderOverviewPcHelp') : t('deviationAnalysis.renderPerSurfaceHelp')
                }
                checked={renderPerSurface}
                onCheckedChange={(c) => set('deviation.renderPerSurface', c)}
                disabledReason={deviationOn ? undefined : needs('deviation', 'renders')}
              />
            </div>
          </AddOnGroup>
        )}

        <AddOnGroup title={t('compareSurfaces.addOns.reportExport')} testId='compare-addons-group-report'>
          <AddOnToggle
            id={pdfId}
            testId='compare-addon-pdf'
            label={t('analysisConfig.addOns.pdfReport')}
            gate='report.pdf'
            checked={addOns.pdf}
            onCheckedChange={(c) => set('addOns.pdf', c)}
            disabledReason={pdfDisabledReason}>
            <p className='text-xs text-muted-foreground'>
              {cutFillOn && deviationOn
                ? t('compareSurfaces.addOns.pdfPairedHint')
                : cutFillOn
                  ? t('report.surfaceDifferenceHint')
                  : t('report.alsoGenerateHint')}
            </p>
            {cutFillOn && stationGate.allowed && stationValues.stationVolumesEnabled && (
              <p className='text-xs text-muted-foreground'>{t('stationVolumes.dialog.reportIncludesSection')}</p>
            )}
            <FormField
              control={form.control}
              name='addOns.reportTitle'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('report.titleLabel')}</FormLabel>
                  <FormControl>
                    <Input data-testid='compare-report-title' type='text' placeholder={runName} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='addOns.reportNotes'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('report.notesLabel')}</FormLabel>
                  <FormControl>
                    <Textarea data-testid='compare-report-notes' rows={3} {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='addOns.reportLocale'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('report.languageLabel')}</FormLabel>
                  <FormControl>
                    <select
                      data-testid='compare-report-locale'
                      className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none'
                      {...field}>
                      {SUPPORTED_REPORT_LOCALES.map((l) => (
                        <option key={l} value={l}>
                          {REPORT_LOCALE_NAMES[l]}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                </FormItem>
              )}
            />
          </AddOnToggle>

          {/* One switch for the run (spec §7), applied to each ticked result;
            the deviation raster's resolution / statistic / SRID are under
            Advanced. */}
          <AddOnToggle
            id={tifId}
            testId='compare-addon-geotiff'
            label={t('analysisConfig.addOns.exportGeotiff')}
            checked={addOns.geotiff}
            onCheckedChange={(c) => set('addOns.geotiff', c)}
          />
        </AddOnGroup>
      </div>
    </AnalysisSection>
  );
}
